/**
 * Follow-up drawer — the right-hand tabs (drawer redesign Phase 6a, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-drawer-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Nilay 2026-10-02: "Design tabs, today's data." The design's tab names and order per queue (the
 * export's DTABS), filled ONLY from what today's drawer (CreateFollowup.vue, FROZEN) already loads
 * for that queue. A design tab with no data source today is LEFT OUT (omittedTabsFor) and listed as
 * follow-on in the plan. Each tab is a list of sections; each section is one of today's tabs /
 * panels, named by its legacy title, reading one key of drawerQueues.readDrawerData().
 *
 * Kept from today:
 *   • Recall's family recall rows leave the patient out (familyApptsWithoutCurrentPerson, :3636);
 *     unscheduled treatment's do NOT (:3698) — the patient's own row shows there.
 *   • Recall's Insurance shows only when the first row's `insurance` is not null, and at most the
 *     primary and secondary rows (template :1362–1388).
 *   • Recall's Activity includes the Auto Recall Reminder panel only when APPT_REMINDER_PAID_FOR
 *     is 'TRUE' (:3583) — an empty reminder list still shows its "none sent yet" state.
 * Today's "Tasks" tab (tasks tagged with the patient, from the tasks store, every queue) has no
 * design tab: LEGACY_ONLY_TABS, for 6b to place.
 */

export const TAB_LABELS = {
  activity: 'Activity',
  comm: 'Comm log',
  appts: 'Appointments',
  tx: 'Treatment',
  family: 'Family',
  ins: 'Insurance',
  claim: 'Claim',
  history: 'Status history',
  preauth: 'Pre-auth',
  famclaims: 'Family claims',
  broken: 'Broken history',
};

/** The export's DTABS, by its queue key. */
export const DESIGN_TABS = {
  or: ['activity', 'comm', 'appts', 'tx', 'family', 'ins'],
  ut: ['activity', 'tx', 'appts', 'comm', 'family', 'ins'],
  claims: ['activity', 'claim', 'history', 'comm', 'ins'],
  preauth: ['activity', 'preauth', 'comm', 'ins'],
  balances: ['activity', 'comm', 'famclaims', 'ins'],
  broken: ['activity', 'broken', 'appts', 'comm', 'ins'],
};

/** detailPageName → the export's queue key. Processed pre-auths are not in the export; they use 'preauth'. */
export const DESIGN_KEY = {
  OverdueRecallDetails: 'or',
  unscheduledPatientDetails: 'ut',
  AgingDetails: 'claims',
  OutstandingPreAuthDetails: 'preauth',
  ProcessedPreAuthDetails: 'preauth',
  PatientBalancesDetails: 'balances',
  BrokenAppointmentDetails: 'broken',
};

const ATTEMPTS = { key: 'attempts', legacyTitle: 'Follow-up Activity', source: 'activities' };
const COMMLOG = { key: 'commlog', legacyTitle: 'Communication Log', source: 'commlog' };

/** Today's data behind each design tab, per queue. A tab missing here has no source for that queue. */
export const TAB_SOURCES = {
  OverdueRecallDetails: {
    activity: [ATTEMPTS, { key: 'reminders', legacyTitle: 'Auto Recall Reminder Activity', source: 'autoRecallReminders', when: 'remindersPaidFor' }],
    comm: [COMMLOG],
    appts: [
      { key: 'scheduled', legacyTitle: 'Scheduled Appointments', source: 'scheduledAppointments' },
      { key: 'past', legacyTitle: 'Past Appointments', source: 'pastAppointments' },
    ],
    tx: [{ key: 'pending', legacyTitle: 'Unscheduled Treatment', source: 'pendingTx' }],
    family: [
      { key: 'recall', legacyTitle: 'Family Recall Appointments', source: 'familyAppts', withoutPatient: true },
      { key: 'pendingTx', legacyTitle: 'Family Pending Tx', source: 'familyPendingTx' },
      { key: 'members', legacyTitle: 'Family Members', source: 'familyMembers' },
    ],
    ins: [{ key: 'insurance', legacyTitle: 'Insurance Info', source: 'insurance', when: 'insuranceNamed', max: 2 }],
  },
  unscheduledPatientDetails: {
    activity: [ATTEMPTS],
    tx: [{ key: 'inPlan', legacyTitle: 'Unscheduled Treatment', source: 'txInPlan' }],
    appts: [
      { key: 'planned', legacyTitle: 'Planned Appointments', source: 'plannedAppointments' },
      { key: 'scheduled', legacyTitle: 'Scheduled Appointments', source: 'scheduledAppointments' },
    ],
    comm: [COMMLOG],
    family: [
      { key: 'recall', legacyTitle: 'Family Recall Appointments', source: 'familyRecallAppointments' },
      { key: 'pendingTx', legacyTitle: 'Family Pending Tx', source: 'familyPendingTx' },
    ],
  },
  AgingDetails: {
    activity: [ATTEMPTS],
    claim: [{ key: 'claim', legacyTitle: 'Claim Information', source: 'claim', single: true }],
    history: [{ key: 'history', legacyTitle: 'Claim Status History', source: 'claimStatusHistory' }],
    comm: [COMMLOG],
  },
  OutstandingPreAuthDetails: {
    activity: [ATTEMPTS],
    preauth: [{ key: 'preauth', legacyTitle: 'Outstanding PreAuthorization Info', source: 'claim', single: true }],
    comm: [COMMLOG],
  },
  ProcessedPreAuthDetails: {
    activity: [ATTEMPTS],
    preauth: [{ key: 'procedures', legacyTitle: 'PreAuthorization Info', source: 'procedures' }],
    comm: [COMMLOG],
  },
  PatientBalancesDetails: {
    activity: [ATTEMPTS],
    comm: [COMMLOG],
    famclaims: [{ key: 'familyClaims', legacyTitle: 'Outstanding Claims for Family', source: 'familyClaims' }],
  },
  BrokenAppointmentDetails: {
    activity: [ATTEMPTS],
    broken: [{ key: 'history', legacyTitle: 'Broken Appointment History', source: 'brokenHistory' }],
    comm: [COMMLOG],
  },
};

/** Today's tabs the design has no slot for. */
export const LEGACY_ONLY_TABS = [
  { key: 'tasks', legacyTitle: 'Tasks', source: 'tasks/getTasksTaggedWithPatient (store, not the query result)' },
];

/**
 * Where today's "Tasks" tab goes in the new drawer (6b): LAST, as its own "Tasks" tab, and only when
 * the patient has tagged tasks — today's rule (`v-if="taggedTasks && taggedTasks.length > 0"`,
 * template :975). Not folded into Activity: those are follow-up ATTEMPTS on this queue; tagged tasks
 * are team to-dos about the patient from the tasks store, on every queue. Last, so the design's own
 * tab order is untouched. `taggedTasks` = the tasks store's getTasksTaggedWithPatient (the container's).
 */
export const TASKS_TAB_LABEL = 'Tasks';

export function withTasksTab(tabs, taggedTasks) {
  const list = Array.isArray(tabs) ? tabs.slice() : [];
  if (!Array.isArray(taggedTasks) || taggedTasks.length === 0) return list;
  list.push({
    key: 'tasks',
    label: TASKS_TAB_LABEL,
    count: taggedTasks.length,
    empty: false,
    sections: [{ key: 'tasks', legacyTitle: LEGACY_ONLY_TABS[0].legacyTitle, rows: taggedTasks, count: taggedTasks.length, shown: true }],
  });
  return list;
}

/** The fields the Claim / Pre-auth tabs print — the PHI ones must be covered by utils/phiMask (phi-mask-core). */
export const CLAIM_TAB_FIELDS = {
  claim: ['ProviderName', 'DateSent', 'DateService', 'Birthdate', 'Zip', 'ProceduresAttached', 'InsPayEst', 'ClaimType',
    'SubScriberID', 'SubscriberName', 'SubscriberBirthDate', 'CarrierName', 'CarrierPhone'],
  preauth: ['DateSent', 'Provider', 'AttachedProcedures', 'Carrier', 'Phone', 'SubscriberName', 'SubscriberBirthDate', 'SubScriberID'],
};

/** Design tabs left out for a queue because today's drawer loads nothing for them. */
export function omittedTabsFor(detailPageName) {
  const design = DESIGN_TABS[DESIGN_KEY[detailPageName]];
  const sources = TAB_SOURCES[detailPageName];
  if (!design || !sources) return [];
  return design.filter(k => !sources[k]);
}

function sectionRows(section, data, { patNum, remindersPaidFor }) {
  const raw = data ? data[section.source] : null;
  if (section.when === 'remindersPaidFor') return remindersPaidFor && raw ? raw : null;
  if (section.single) return raw ? [raw] : [];
  if (!raw) return section.when === 'insuranceNamed' ? null : [];
  if (section.when === 'insuranceNamed' && !(raw.length > 0 && raw[0].insurance !== null)) return null;
  let rows = raw;
  if (section.withoutPatient) rows = rows.filter(p => p.PatNum !== parseInt(patNum, 10));
  if (section.max) rows = rows.slice(0, section.max);
  return rows;
}

/**
 * The tab model for one queue: design order, design labels, today's data.
 * → [{ key, label, count, empty, sections: [{ key, legacyTitle, rows, count, shown }] }]
 * `data` = drawerQueues.readDrawerData(); `remindersPaidFor` = APPT_REMINDER_PAID_FOR === 'TRUE'.
 * A section that today hides itself (reminders not paid for, insurance with no carrier) has
 * `shown: false`, rows null and count 0.
 */
export function buildDrawerTabs({ detailPageName, data, patNum, remindersPaidFor } = {}) {
  const design = DESIGN_TABS[DESIGN_KEY[detailPageName]];
  const sources = TAB_SOURCES[detailPageName];
  if (!design || !sources) return [];
  return design.filter(k => sources[k]).map((key) => {
    const sections = sources[key].map((section) => {
      const rows = sectionRows(section, data, { patNum, remindersPaidFor });
      const count = rows ? rows.length : 0;
      return { key: section.key, legacyTitle: section.legacyTitle, rows, count, shown: rows !== null };
    });
    const count = sections.reduce((n, s) => n + s.count, 0);
    return { key, label: TAB_LABELS[key], count, empty: count === 0, sections };
  });
}
